import { defineCommand } from '../../../../concepts/commands/CommandDefinition';
import { ObjectManager } from '../../../../concepts/objects';
import { AdapterInfo, CreateAdapterInfo } from '../../../canvas/AdapterInfo';
import { place } from '../../../canvas/commands/OrderCommands';
import { EditorSession } from '../../../canvas/EditorSession';
import { Physical, RelativeTo } from '../../../canvas/entities/Physical';
import { SpatialIndex } from '../../../canvas/utilities/SpatialIndex';
import { Frame } from '../Frame';
import { FrameAddonWithDimensions } from './FrameAddonWithDimensions';

export const frameEditAddonDimensions = defineCommand(
    'frameEditAddonDimensions',
    'Frame: Edit Addon Dimensions',
    (cc, [id]: [string]) => {

        const objects = cc.resolve(ObjectManager);
        const addon = objects.getMaybe(id, FrameAddonWithDimensions);
        const frame = !!addon ? addon.parentFrame : objects.getMaybe(id, Frame);
        if (!frame) return;

        const session = cc.resolve(EditorSession);
        session.update(AdapterInfo, CreateAdapterInfo(frame.id, 'frame'));
    }
);

export const frameAdjustAddonDimensions = defineCommand(
    'frameAdjustAddonDimensions',
    'Frame: Adjust Addon Dimensions',
    (cc, [id, actionKey]: [string, string]) => {

        const objects = cc.call(ObjectManager);
        const spatial = cc.call(SpatialIndex);
        const frame = objects.getMaybe(id, Frame);
        if (!frame) return;

        const addon = frame.addon().as(FrameAddonWithDimensions);
        if (!addon) return;

        const [cols, rows] = addon.dimensions || [1, 1];
        const { w, h } = frame.size().divide([Math.max(cols, 1), Math.max(rows, 1)]);
        const [mode, dim, edge] = actionKey.split('-');

        objects.update(() => {

            // Add the dim
            const inc = mode === 'add' ? 1 : -1;
            const [nextCols, nextRows] =
                (dim === 'col' ? [cols + inc, rows] : null) ??
                (dim === 'row' ? [cols, rows + inc] : null) ??
                [cols, rows];

            addon.dimensions = [Math.max(1, nextCols) || 1, Math.max(1, nextRows)];
            addon.offsetDimensions(
                dim === 'col' && edge === 'start' ? inc : 0,
                dim === 'row' && edge === 'start' ? inc : 0,
            );

            // Resize the frame to include the additional size
            const oldBounds = frame.bounds(RelativeTo.Model);
            const newBounds = oldBounds.extend(
                dim === 'col' && edge === 'start' ? (w * inc) : 0,
                dim === 'row' && edge === 'start' ? (h * inc) : 0,
                dim === 'col' && edge === 'end' ? (w * inc) : 0,
                dim === 'row' && edge === 'end' ? (h * inc) : 0,
            );

            frame.setFromFigure(newBounds.points());

            // If edge is left or top, we need to offset all the children by the size of the new dimensions
            if (edge === 'start') {
                for (const ch of frame.children) {
                    const ph = ch.as(Physical);
                    if (!ph) continue;
                    ph.moveBy(
                        dim === 'col' ? (w * inc) : 0,
                        dim === 'row' ? (h * inc) : 0,
                    );
                }
            }

            // If increased in size, swallow (oo-er)
            if (mode === 'add') {
                const opts = Array.from(spatial.lookup(newBounds));
                const ids = Array.from(spatial.lookup(newBounds)).reduce((hits, ph) => {
                    if (ph === frame || ph.isDescendantOf(frame)) return hits;
                    return [...hits, ph.id];
                }, []);

                if (ids.length) {
                    cc.dispatch(place, [ids])
                }
            }
            // If reduced in size, place and children that are now outside the frame
            else if (mode === 'rem') {
                const ids = frame.children.reduce((hits, ch) => {
                    const ph = ch.as(Physical);
                    if (!ph) return hits;
                    if (newBounds.contains(ph.bounds(RelativeTo.Model))) return hits;
                    return [...hits, ph.id];
                }, []);

                if (ids.length) {
                    cc.dispatch(place, [ids])
                }
            }
        });

    },
);

export const frameSetAddonDimensions = defineCommand(
    'frameSetAddonDimensions',
    'Frame: Set Addon Dimensions',
    (cc, [id, nextCols, nextRows]: [string, number, number]) => {

        const objects = cc.call(ObjectManager);
        const spatial = cc.call(SpatialIndex);
        const frame = objects.getMaybe(id, Frame);
        if (!frame) return;

        const addon = frame.addon().as(FrameAddonWithDimensions);
        if (!addon) return;

        objects.update(() => {

            addon.dimensions = [nextCols, nextRows];

            const os = frame.size();
            const ns = addon.getCorrectedSize(frame.width, frame.height);
            if (os.equals(ns)) return;

            frame.resize(ns.w, ns.h);
            cc.dispatch(place, [frame.id]);

            const newBounds = frame.bounds(RelativeTo.Model);

            // If increased in size, swallow (oo-er)
            if (ns.w > os.w || ns.h > os.h) {
                const opts = Array.from(spatial.lookup(newBounds));
                const ids = Array.from(spatial.lookup(newBounds)).reduce((hits, ph) => {
                    if (ph === frame || ph.isDescendantOf(frame)) return hits;
                    return [...hits, ph.id];
                }, []);

                if (ids.length) {
                    cc.dispatch(place, [ids])
                }
            }
            // If reduced in size, place and children that are now outside the frame
            else if (ns.w < os.w || ns.h < os.h) {
                const ids = frame.children.reduce((hits, ch) => {
                    const ph = ch.as(Physical);
                    if (!ph) return hits;
                    if (newBounds.contains(ph.bounds(RelativeTo.Model))) return hits;
                    return [...hits, ph.id];
                }, []);

                if (ids.length) {
                    cc.dispatch(place, [ids])
                }
            }

        });

    },
);
